import { useTranslation } from 'react-i18next';

import { IconBell } from '@tabler/icons-react';

import {
  ActionIcon,
  Badge,
  Box,
  Divider,
  EmptyStateView,
  Group,
  Indicator,
  Loader,
  Menu,
  ScrollArea,
  Stack,
  Text,
} from '@optiro/ui';

import { NotificationItem } from './NotificationItem';
import { NotificationType } from './notifications.types';

interface NotificationsViewProps {
  notifications: NotificationType[];
  unreadCount: number;
  isLoading: boolean;
  opened: boolean;
  onOpenChange: (opened: boolean) => void;
  onNotificationClick: (n: NotificationType) => void;
}

export function NotificationsView({
  notifications,
  unreadCount,
  isLoading,
  opened,
  onOpenChange,
  onNotificationClick,
}: NotificationsViewProps) {
  const { t } = useTranslation('notifications');

  return (
    <Menu
      opened={opened}
      onChange={onOpenChange}
      position="bottom-end"
      width={380}
      withinPortal
      shadow="lg"
      radius="md"
    >
      <Menu.Target>
        <Indicator disabled={unreadCount === 0} color="red" size={8} offset={4} processing>
          <ActionIcon variant="subtle" size="lg">
            <IconBell stroke={1.5} size={22} />
          </ActionIcon>
        </Indicator>
      </Menu.Target>

      <Menu.Dropdown p={0}>
        <Group justify="space-between" px="md" py="sm">
          <Text fw={700} size="sm">
            {t('title', 'Notifikationer')}
          </Text>
          {unreadCount > 0 && (
            <Badge color="blue" variant="light" size="sm" radius="xl">
              {unreadCount}
            </Badge>
          )}
        </Group>
        <Divider />

        <Box px="xs" py="xs">
          {isLoading ? (
            <Group justify="center" py="xl">
              <Loader size="sm" />
            </Group>
          ) : notifications.length === 0 ? (
            <EmptyStateView
              variant="inline"
              title={t('empty', 'Ingen notifikationer')}
              illustration={<IconBell size={16} />}
            />
          ) : (
            <ScrollArea.Autosize mah={400}>
              <Stack gap={4}>
                {notifications.map((n) => (
                  <NotificationItem key={n.id} n={n} onItemClick={onNotificationClick} />
                ))}
              </Stack>
            </ScrollArea.Autosize>
          )}
        </Box>
      </Menu.Dropdown>
    </Menu>
  );
}
